import { useCallback, useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import URLS from 'constants/urls';
import { reportError, reportWarning } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { selectActiveVenue } from 'selectors/general';

import { isCancelledOrderItem } from '../shared/SkuOrderFlyout.helpers';

import { fetchPartnerOrders } from './SkuOrders.api';
import { DEFAULT_PARTNER_ORDERS_PAGE_SIZE, LIFECYCLE_STATUS, PARTNER_ORDERS_TAB } from './SkuOrders.constants';
import { skuOrdersKeys } from './skuOrdersKeys';

export function getPartnerOrdersPageRange(page, ordersCount, pageSize = DEFAULT_PARTNER_ORDERS_PAGE_SIZE) {
  const startNumber = ordersCount > 0 ? (page - 1) * pageSize + 1 : 0;
  const endNumber = ordersCount > 0 ? startNumber + ordersCount - 1 : 0;

  return { startNumber, endNumber };
}

const roundMoneyAmount = amount => Math.round(amount * 100) / 100;

function patchOrderCancelled(order, { cancellationType, orderItemIds, cancelledAt, expectedAmount }) {
  const targetIds = orderItemIds?.length ? new Set(orderItemIds) : null;
  let cancelledBaseAmount = 0;
  const orderItems = (order.orderItems || []).map(item => {
    const isTarget = !targetIds || targetIds.has(item?.core?.orderItemId);
    if (!isTarget || isCancelledOrderItem(item)) return item;
    cancelledBaseAmount += item?.basePayableAmount?.amount ?? 0;
    return { ...item, core: { ...item.core, cancelledAt, cancellationType } };
  });

  let { payableAmount } = order;
  if (targetIds && payableAmount) {
    const reducedAmount = Math.max(0, roundMoneyAmount(payableAmount.amount - cancelledBaseAmount));
    payableAmount = { ...payableAmount, amount: Math.min(reducedAmount, expectedAmount ?? reducedAmount) };
  }

  return {
    ...order,
    lifecycleStatus: orderItems.every(isCancelledOrderItem) ? LIFECYCLE_STATUS.CANCELLED : order.lifecycleStatus,
    orderItems,
    payableAmount,
  };
}

// The cancel is accepted before a worker cancels the rows, and payments lowers the order total after that, so a poll
// inside that window would undo the optimistic patch. Accepted cancels are kept here until the server reflects them.
const PENDING_CANCEL_TTL_MS = 2 * 60 * 1000;
const pendingCancelsByClient = new WeakMap();

const getPendingCancels = queryClient => pendingCancelsByClient.get(queryClient) ?? [];

function isReflectedByServer(order, { orderItemIds, expectedAmount }) {
  const targetIds = orderItemIds?.length ? new Set(orderItemIds) : null;
  const rowsCancelled = (order.orderItems || [])
    .filter(item => !targetIds || targetIds.has(item?.core?.orderItemId))
    .every(isCancelledOrderItem);
  const totalCaughtUp = expectedAmount == null || (order.payableAmount?.amount ?? 0) <= expectedAmount;
  return rowsCancelled && totalCaughtUp;
}

export function reapplyPendingCancels(queryClient, venueId, data, now = Date.now()) {
  const pending = getPendingCancels(queryClient).filter(entry => now - entry.startedAt < PENDING_CANCEL_TTL_MS);
  const settled = new Set();

  const orders = (data?.orders ?? []).map(order =>
    pending
      .filter(entry => entry.venueId === venueId && entry.orderId === order?.core?.orderId)
      .reduce((patched, entry) => {
        if (isReflectedByServer(patched, entry)) {
          settled.add(entry);
          return patched;
        }
        return patchOrderCancelled(patched, entry);
      }, order)
  );

  pendingCancelsByClient.set(
    queryClient,
    pending.filter(entry => !settled.has(entry))
  );
  return pending.length ? { ...data, orders } : data;
}

export function usePartnerOrders(activeTab, options = {}) {
  const { lifecycleStatus = null, ...queryOptions } = options;
  const { venueId, timezone: venueTimezone, venueName } = useSelector(selectActiveVenue);
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [cursorByPage, setCursorByPage] = useState({ 1: null });

  useEffect(() => {
    setPage(1);
    setCursorByPage({ 1: null });
  }, [venueId, activeTab, lifecycleStatus]);

  const cursor = cursorByPage[page] ?? null;

  const query = useQuery({
    queryKey: skuOrdersKeys.list(venueId, activeTab, cursor, lifecycleStatus),
    queryFn: async () =>
      reapplyPendingCancels(
        queryClient,
        venueId,
        await fetchPartnerOrders({ venueId, tab: activeTab, venueTimezone, venueName, cursor, lifecycleStatus })
      ),
    enabled: !!venueId,
    ...queryOptions,
  });

  const orders = query.data?.orders ?? [];
  const nextPageCursor = query.data?.nextPageCursor ?? null;

  const changePage = useCallback(
    nextPage => {
      if (nextPage > page && !nextPageCursor) return;

      if (nextPage > page) {
        setCursorByPage(previous => ({
          ...previous,
          [nextPage]: nextPageCursor,
        }));
      }
      setPage(nextPage);
    },
    [nextPageCursor, page]
  );

  const showPagination = page > 1 || !!nextPageCursor;
  const { startNumber, endNumber } = getPartnerOrdersPageRange(page, orders.length);

  return {
    ...query,
    orders,
    page,
    changePage,
    endNumber,
    nextPageCursor,
    showPagination,
    startNumber,
  };
}

export function useInvalidatePartnerOrders() {
  const queryClient = useQueryClient();
  return useCallback(() => queryClient.invalidateQueries({ queryKey: skuOrdersKeys.all }), [queryClient]);
}

const HTTP_STATUS_CONFLICT = 409;
const HTTP_STATUS_UNPROCESSABLE = 422;
const MENU_UPDATE_FAILED_WARNING = 'menu_update_failed';
const ORDER_ITEMS_ALREADY_CANCELLED = 'ORDER_ITEMS_ALREADY_CANCELLED';

export const CANCEL_ORDER_CONFLICT = {
  ALREADY_CANCELLED: 'already_cancelled',
  ORDER_CHANGED: 'order_changed',
};

const isMenuUpdateFailedResponse = error =>
  error?.response?.status === HTTP_STATUS_UNPROCESSABLE && error.response.data?.warning === MENU_UPDATE_FAILED_WARNING;

export function getCancelOrderConflict(error) {
  if (error?.response?.status !== HTTP_STATUS_CONFLICT) return null;
  return error.response.data?.name === ORDER_ITEMS_ALREADY_CANCELLED
    ? CANCEL_ORDER_CONFLICT.ALREADY_CANCELLED
    : CANCEL_ORDER_CONFLICT.ORDER_CHANGED;
}

// CancelOrder is async server-side; patch the react-query cache immediately after the cancel is accepted.
export function applyOptimisticOrderCancelled(queryClient, { venueId, orderId, cancellationType, orderItemIds }) {
  if (!venueId || !orderId || !cancellationType) return;

  const entry = { venueId, orderId, cancellationType, orderItemIds, cancelledAt: new Date().toISOString() };
  let expectedAmount = null;

  const updater = oldData => {
    if (!oldData?.orders?.length) return oldData;

    return {
      ...oldData,
      orders: oldData.orders.map(order => {
        if (order?.core?.orderId !== orderId) return order;

        const patched = patchOrderCancelled(order, entry);
        if (orderItemIds?.length && patched.payableAmount) expectedAmount = patched.payableAmount.amount;
        return patched;
      }),
    };
  };

  [PARTNER_ORDERS_TAB.TODAY, PARTNER_ORDERS_TAB.HISTORY].forEach(tab => {
    queryClient.setQueriesData({ queryKey: skuOrdersKeys.listPrefix(venueId, tab) }, updater);
  });

  pendingCancelsByClient.set(queryClient, [
    ...getPendingCancels(queryClient),
    { ...entry, expectedAmount, startedAt: Date.now() },
  ]);
}

export function useApplyOptimisticOrderCancelled() {
  const queryClient = useQueryClient();
  const { venueId } = useSelector(selectActiveVenue);

  return useCallback(
    (orderId, cancellationType, orderItemIds) =>
      applyOptimisticOrderCancelled(queryClient, { venueId, orderId, cancellationType, orderItemIds }),
    [queryClient, venueId]
  );
}

export function useFulfillSkuOrder(options = {}) {
  return useMutation({
    mutationFn: async orderId => {
      try {
        const url = URLS.skuOrders.fulfillOrder(orderId);
        const response = await httpClient.post(url, {}, { ignoreInternalError: true });
        return response?.data ?? null;
      } catch (error) {
        reportError(error);
        throw error;
      }
    },
    ...options,
  });
}

export function useCancelSkuOrder(options = {}) {
  return useMutation({
    mutationFn: async ({ orderId, cancellationType, orderItemIds }) => {
      const url = URLS.skuOrders.cancelOrder(orderId);
      const body = { cancellation_type: cancellationType };
      if (orderItemIds?.length) body.order_item_ids = orderItemIds;
      try {
        const response = await httpClient.post(url, body, { ignoreInternalError: true });
        return response?.data ?? null;
      } catch (error) {
        // The backend enqueues the cancellation before flipping the item sold out, so a 422 is still a cancel.
        if (isMenuUpdateFailedResponse(error)) {
          reportWarning('SKU order cancellation accepted but the sold-out menu update failed', {
            orderId,
            orderItemIds,
          });
          return { menuUpdateFailed: true };
        }
        reportError(error);
        throw error;
      }
    },
    ...options,
  });
}
